type EnvironmentName = 'local' | 'staging' | 'production' | 'training';

export function resolveEnvironmentName(): EnvironmentName {
  if (typeof window === 'undefined') {
    return 'local';
  }
  const { hostname } = window.location;
  if (hostname === 'app.prospero.food') {
    return 'production';
  }
  if (hostname === 'prospero.queuespark.com') {
    return 'staging';
  }
  if (hostname === 'training.prospero.food') {
    return 'training';
  }

  const raw = process.env.PUBLIC_ENV_NAME ?? '';
  const valid: ReadonlySet<string> = new Set<EnvironmentName>([
    'local',
    'staging',
    'production',
    'training',
  ]);
  return valid.has(raw) ? (raw as EnvironmentName) : 'local';
}

export const ENV_NAME = resolveEnvironmentName();
export const IS_LOCAL = ENV_NAME === 'local';
const IS_PRODUCTION = ENV_NAME === 'production';
const IS_TRAINING = ENV_NAME === 'training';

export { IS_PRODUCTION, IS_TRAINING };

// Raw values — no `as string` casts
const rawClerkKey: string | undefined = IS_PRODUCTION
  ? process.env.PUBLIC_PROD_CLERK_PUBLISHABLE_KEY
  : IS_TRAINING
    ? process.env.PUBLIC_TRAINING_CLERK_PUBLISHABLE_KEY
    : process.env.PUBLIC_STAGING_CLERK_PUBLISHABLE_KEY;

const rawApiUrl: string | undefined = process.env.PUBLIC_API_URL;

const rawGtmId: string | undefined = IS_PRODUCTION
  ? process.env.PUBLIC_PROD_GTM_ID
  : IS_TRAINING
    ? process.env.PUBLIC_TRAINING_GTM_ID
    : process.env.PUBLIC_STAGING_GTM_ID;

const GTM_ID_RE = /^GTM-[A-Z0-9]+$/;

const CLERK_KEY_RE = /^pk_(test|live)_[A-Za-z0-9_-]+$/;

export function isValidClerkKey(
  value: string | undefined,
  isProd: boolean,
): { ok: true } | { ok: false; detail: string } {
  const trimmed = (value ?? '').trim();
  if (trimmed.length === 0) {
    return { ok: false, detail: 'value is empty or undefined' };
  }
  if (!CLERK_KEY_RE.test(trimmed)) {
    return {
      ok: false,
      detail: 'expected pk_test_/pk_live_ prefix followed by alphanumeric chars',
    };
  }
  if (isProd && trimmed.startsWith('pk_test_')) {
    return { ok: false, detail: 'production environment requires pk_live_ key' };
  }
  return { ok: true };
}

function isValidApiUrl(value: string | undefined): { ok: true } | { ok: false; detail: string } {
  const trimmed = (value ?? '').trim();
  if (trimmed.length === 0) {
    return { ok: false, detail: 'value is empty or undefined' };
  }
  if (trimmed === 'undefined') {
    return { ok: false, detail: 'literal string "undefined" is not a valid URL' };
  }
  if (trimmed.startsWith('/')) {
    return { ok: true };
  }
  try {
    new URL(trimmed);
    return { ok: true };
  } catch {
    return { ok: false, detail: 'value is not a relative path or a valid absolute URL' };
  }
}

export interface MissingConfigInfo {
  reason: string;
  field: 'CLERK_PUBLISHABLE_KEY' | 'API_URL';
  detail: string;
  envName: EnvironmentName;
}

function computeMissingConfig(): MissingConfigInfo | null {
  const clerkResult = isValidClerkKey(rawClerkKey, IS_PRODUCTION);
  if (!clerkResult.ok) {
    return {
      reason: 'CLERK_PUBLISHABLE_KEY is missing or malformed',
      field: 'CLERK_PUBLISHABLE_KEY',
      detail: clerkResult.detail,
      envName: ENV_NAME,
    };
  }

  const apiResult = isValidApiUrl(rawApiUrl);
  if (!apiResult.ok) {
    return {
      reason: 'API_URL is missing or malformed',
      field: 'API_URL',
      detail: apiResult.detail,
      envName: ENV_NAME,
    };
  }

  return null;
}

export const MISSING_CONFIG: MissingConfigInfo | null = computeMissingConfig();

export const CLERK_PUBLISHABLE_KEY: string = rawClerkKey ?? '';

export const API_URL: string = rawApiUrl || '/api';

export const SENTRY_DSN: string | undefined = process.env.PUBLIC_SENTRY_DSN;

/**
 * GTM container that distributes analytics + advertising pixels. Optional on
 * purpose: an invalid/absent id disables tag loading entirely (analytics events
 * still reach `window.dataLayer` and are visible in debug logs locally), so
 * GTM onboarding for a new environment never blocks the app.
 */
const gtmIdTrimmed = (rawGtmId ?? '').trim();
export const GTM_ID: string | undefined =
  !IS_LOCAL && !IS_TRAINING && GTM_ID_RE.test(gtmIdTrimmed) ? gtmIdTrimmed : undefined;
